import type {
  AgentDomain,
  AgentKind,
  AgentProfile,
  AgentProviderType,
  AgentRole,
  AgentType,
  ConversationMessage,
  MemoryScope,
} from "@/lib/aura/types";

type AuraConversationWireMessage = {
  role: "user" | "assistant";
  text: string;
  createdAt?: string;
};

export type AuraConversationResult = {
  replyText: string;
  channel?: {
    requestedAgent: AdminConsoleAgentTarget;
    respondedBy: AdminConsoleAgentTarget;
    responderRuntime: string;
    proxy: boolean;
    authority: "read_only";
    evidenceSource: "climb_one_core";
  };
  session?: AuraConversationSession;
  persistedMessages: ConversationMessage[];
  contextCards: Array<{
    kind: string;
    title: string;
    detail: string;
  }>;
};

export type AuraConversationSession = {
  schemaVersion: 1;
  id: string;
  title: string;
  targetAgent: AdminConsoleAgentTarget;
  createdAt: string;
  updatedAt: string;
  messageCount: number;
  lastMessageAt?: string;
};

export async function askAuraConversation({
  messageText,
  recentMessages,
  targetAgent = "aura",
  sessionId,
}: {
  messageText: string;
  recentMessages: ConversationMessage[];
  targetAgent?: AgentType | "all";
  sessionId?: string;
}): Promise<AuraConversationResult> {
  const wireTarget = toAdminConsoleAgentTarget(targetAgent);
  const response = await fetch("/admin-console/aura/conversation", {
    method: "POST",
    headers: {
      "content-type": "application/json",
    },
    body: JSON.stringify({
      messageText,
      targetAgent: wireTarget,
      sessionId: sessionId ?? createCommandSessionId(targetAgent),
      recentMessages: recentMessages.slice(-8).map(toAuraConversationMessage),
    }),
  });
  const payload = await response.json();

  if (!response.ok || !payload.conversation?.reply?.text) {
    throw new Error(payload.error ?? "aura_conversation_failed");
  }

  return {
    replyText: payload.conversation.reply.text,
    channel: payload.conversation.channel,
    session: payload.conversation.session,
    persistedMessages: Array.isArray(payload.conversation.persistedMessages)
      ? payload.conversation.persistedMessages.map(toConversationMessage)
      : [],
    contextCards: payload.conversation.context?.cards ?? [],
  };
}

export async function loadAuraConversationHistory({
  targetAgent,
  sessionId,
  limit = 120,
}: {
  targetAgent?: AgentType | "all";
  sessionId?: string;
  limit?: number;
}): Promise<{
  sessions: AuraConversationSession[];
  messages: ConversationMessage[];
}> {
  const params = new URLSearchParams();
  if (targetAgent) params.set("targetAgent", toAdminConsoleAgentTarget(targetAgent));
  if (sessionId) params.set("sessionId", sessionId);
  params.set("limit", String(limit));

  const response = await fetch(`/admin-console/aura/conversation/history?${params}`, {
    cache: "no-store",
  });
  const payload = await response.json();

  if (!response.ok || !payload.conversationHistory) {
    throw new Error(payload.error ?? "aura_conversation_history_failed");
  }

  return {
    sessions: payload.conversationHistory.sessions ?? [],
    messages: Array.isArray(payload.conversationHistory.messages)
      ? payload.conversationHistory.messages.map(toConversationMessage)
      : [],
  };
}

type AdminConsoleAgentTarget =
  | "aura"
  | "founder_agent"
  | "codex"
  | "claude"
  | "core"
  | "all_agents";

export function toAdminConsoleAgentTarget(targetAgent: AgentType | "all"): AdminConsoleAgentTarget {
  const targets: Record<AgentType | "all", AdminConsoleAgentTarget> = {
    aura: "aura",
    "founder-agent": "founder_agent",
    codex: "codex",
    claude: "claude",
    core: "core",
    all: "all_agents",
  };

  return targets[targetAgent];
}

export function createCommandSessionId(targetAgent: AgentType | "all"): string {
  return `command-${targetAgent}`;
}

export async function recordDecisionContribution({
  contribution,
  contributionKind = "proposal",
  authorityLevel = "advisory",
  blocksExecution = false,
  evidenceReferences,
  topic,
}: {
  contribution: string;
  contributionKind?: "proposal" | "objection";
  authorityLevel?: "advisory" | "blocking_objection" | "core_authority";
  blocksExecution?: boolean;
  topic?: {
    id: string;
    title: string;
    status: string;
  };
  evidenceReferences?: Array<{
    id: string;
    uri: string;
    label: string;
  }>;
}): Promise<{
  id: string;
  recordedAt: string;
  contribution: string;
}> {
  const response = await fetch("/admin-console/decision-room/contributions", {
    method: "POST",
    headers: {
      "content-type": "application/json",
    },
    body: JSON.stringify({
      participant: "Human Founder",
      role: "human",
      source: "admin_console_intake",
      contributionKind,
      contribution,
      authorityLevel,
      blocksExecution,
      rawPayload: topic
        ? {
            topic: {
              id: topic.id,
              title: topic.title,
              status: topic.status,
            },
            recordedFrom: "admin_console_decision_room",
          }
        : undefined,
      evidenceReferences:
        evidenceReferences && evidenceReferences.length > 0
          ? evidenceReferences
          : [
              {
                id: "admin-console-current-state",
                uri: "core://admin-console/operational-state",
                label: "Current Admin Console operational state",
              },
            ],
    }),
  });
  const payload = await response.json();

  if (!response.ok || !payload.contribution) {
    throw new Error(payload.error ?? "decision_room_contribution_failed");
  }

  return {
    id: payload.contribution.id,
    recordedAt: payload.contribution.recordedAt,
    contribution: payload.contribution.contribution,
  };
}

export async function queueOperatorCommand(
  kind:
    | "browser_dry_run"
    | "api_dry_run"
    | "desktop_dry_run"
    | "sap_browser_readiness_dry_run"
    | "sap_api_readiness_dry_run"
    | "sap_desktop_readiness_dry_run",
  metadata?: {
    learningSessionId?: string;
    profileId?: string;
    routineId?: string;
    approvalRecordId?: string;
    requestedScopes?: string[];
  }
): Promise<string> {
  const response = await fetch(`/admin-console/operator-runtime/commands/queue?kind=${kind}`, {
    method: "POST",
    headers: metadata
      ? {
          "content-type": "application/json",
        }
      : undefined,
    body: metadata
      ? JSON.stringify({
          learningSessionId: metadata.learningSessionId,
          profileId: metadata.profileId,
          routineId: metadata.routineId,
          approvalRecordId: metadata.approvalRecordId,
          scopes: metadata.requestedScopes,
        })
      : undefined,
  });
  const payload = await response.json();

  if (!response.ok || !payload.ok) {
    throw new Error(payload.error ?? "operator_command_queue_failed");
  }

  return payload.executionState?.label ?? "Operator command queued through Core.";
}

export async function dispatchNextOperatorCommand(): Promise<string> {
  const response = await fetch("/admin-console/operator-runtime/commands/dispatch-next", {
    method: "POST",
  });
  const payload = await response.json();

  if (!response.ok || !payload.ok) {
    throw new Error(payload.error ?? "operator_command_dispatch_failed");
  }

  return payload.executionState?.label ?? "Operator command dispatch completed.";
}

export async function refreshAdminConsoleSnapshot(): Promise<void> {
  const response = await fetch("/admin-console/refresh", {
    method: "POST",
  });

  if (!response.ok) {
    const payload = await response.json().catch(() => undefined);
    throw new Error(payload?.error ?? "admin_console_refresh_failed");
  }
}

export type AdminAgentCreateInput = {
  displayName: string;
  kind: AgentKind;
  domain: AgentDomain;
  role: AgentRole;
  providerType?: AgentProviderType;
  modelLabel?: string;
  strengths?: string[];
  limitations?: string[];
  memoryScope?: MemoryScope;
};

export type AdminToolVisibility = "private" | "workspace" | "tenant";
export type AdminToolStatus = "draft" | "active" | "paused" | "archived";
export type AdminToolModuleCategory = "technical" | "functional" | "hybrid";
export type AdminToolTeachingMode =
  | "conversation"
  | "document_upload"
  | "screen_recording"
  | "api_trace"
  | "human_demonstration"
  | "routine_replay";
export type AdminToolConnectorType =
  | "browser"
  | "desktop"
  | "api"
  | "sap"
  | "sap_gui"
  | "sap_btp"
  | "sap_bas"
  | "sap_integration_suite"
  | "sap_hana_cloud"
  | "custom";
export type AdminToolApprovalMode =
  | "not_required"
  | "required_before_external_action"
  | "required_before_write"
  | "required_before_production_impact";

export type AdminToolModuleContract = {
  id: string;
  displayName: string;
  category: AdminToolModuleCategory;
  description: string;
  surfaces: string[];
  connectorTypes: AdminToolConnectorType[];
  teachingModes: AdminToolTeachingMode[];
  approvalMode: AdminToolApprovalMode;
  evidenceRequired: string[];
  memoryWritePolicy: "disabled" | "manual_approval" | "automatic_after_verification";
  rawSecretsVisibleByDefault: false;
};

export type AdminToolCatalogRecord = {
  schemaVersion: 1;
  id: string;
  tenantId: string;
  workspaceId: string;
  ownerUserId?: string;
  ownerUserEmail?: string;
  domainId: string;
  decisionRoomToolDomain: string;
  displayName: string;
  description: string;
  status: AdminToolStatus;
  visibility: AdminToolVisibility;
  modules: AdminToolModuleContract[];
  allowedRoleIds: string[];
  createdAt: string;
  updatedAt: string;
  rawSecretsVisibleByDefault: false;
};

export type AdminAgentCognitiveConsolidationPhase = {
  kind: "wake_capture" | "non_rem_replay" | "rem_associative_recombination" | "wake_indexing";
  purpose: string;
  inputSignals: string[];
  outputArtifacts: string[];
};

export type AdminAgentCognitiveConsolidationPolicy = {
  schemaVersion: 1;
  schedule: {
    cadence: "nightly" | "manual" | "after_major_execution";
    timezone: string;
    preferredWindow: string;
  };
  phases: AdminAgentCognitiveConsolidationPhase[];
  sourceEvidence: string[];
  writes: {
    episodicMemory: true;
    semanticMemoryCandidates: true;
    proceduralRoutineCandidates: true;
    contradictionQueue: true;
    rawSecretsAllowed: false;
    providerNativeMemoryAllowed: false;
  };
  approvalRequiredForPromotion: true;
};

export type AdminAgentDaemonContract = {
  schemaVersion: 1;
  id: string;
  runtimeBase: "climb-one-agent-loop-runtime";
  loopStatus: "planned" | "running" | "sleeping" | "consolidating" | "paused" | "failed";
  supervisorAgentId: "agent-founder";
  visibleGovernorAgentId: "agent-aura";
  executorAgentId: string;
  auditorAgentId?: string;
  heartbeatAt?: string;
  nextWakeAt?: string;
  memoryScopes: Array<{
    kind: "tenant" | "workspace" | "agent" | "tool_domain";
    id: string;
    description: string;
  }>;
  cognitiveConsolidation: AdminAgentCognitiveConsolidationPolicy;
  rawSecretsVisibleByDefault: false;
};

export type AdminToolAgentAssignment = {
  schemaVersion: 1;
  id: string;
  tenantId: string;
  workspaceId: string;
  toolId: string;
  moduleIds: string[];
  agentId: string;
  assignedBy: {
    userId?: string;
    userEmail?: string;
    source: "admin_console" | "aura_command" | "core_seed";
  };
  visibility: AdminToolVisibility;
  status: "active" | "paused" | "revoked";
  daemon: AdminAgentDaemonContract;
  decisionRoom: {
    topicKinds: string[];
    toolDomain: string;
    candidateAgents: Array<{
      agentId: string;
      displayName: string;
      role: string;
      domain: string;
      registrationStatus: string;
      score: number;
      reasons: string[];
    }>;
  };
  createdAt: string;
  updatedAt: string;
  rawSecretsVisibleByDefault: false;
};

export type AdminToolLearningSession = {
  schemaVersion: 1;
  id: string;
  tenantId: string;
  workspaceId: string;
  toolId: string;
  moduleId: string;
  assignmentId?: string;
  agentId?: string;
  title: string;
  objective: string;
  teachingMode: AdminToolTeachingMode;
  status: "draft" | "capturing" | "ready_for_review" | "approved_for_memory" | "rejected" | "archived";
  createdBy: {
    userId?: string;
    userEmail?: string;
    source: "admin_console" | "aura_command" | "agent_daemon";
  };
  evidence: Array<{
    id: string;
    kind: "conversation" | "document" | "screen_recording" | "screenshot" | "action_trace" | "api_trace" | "routine_replay" | "human_feedback";
    label: string;
    referenceUri: string;
    sensitivity: "internal" | "sensitive_sanitized" | "secret_redacted";
  }>;
  memoryWriteback: {
    targetScopes: AdminAgentDaemonContract["memoryScopes"];
    promotionStatus: "not_ready" | "candidate_created" | "review_required" | "approved" | "rejected";
    approvalRequired: true;
    rawSecretsAllowed: false;
    providerNativeMemoryAllowed: false;
  };
  createdAt: string;
  updatedAt: string;
  rawSecretsVisibleByDefault: false;
};

export type AdminToolConsolidationRun = {
  schemaVersion: 1;
  id: string;
  tenantId: string;
  workspaceId: string;
  assignmentId: string;
  agentId: string;
  toolId: string;
  status: "planned" | "running" | "completed" | "failed" | "blocked_for_review";
  scheduledFor: string;
  startedAt?: string;
  completedAt?: string;
  phases: Array<{
    kind: AdminAgentCognitiveConsolidationPhase["kind"];
    status: "pending" | "running" | "completed" | "failed" | "blocked_for_review";
    evidenceRefs: string[];
    outputRefs: string[];
  }>;
  memoryPromotionRequired: boolean;
  rawSecretsVisibleByDefault: false;
};

export type AdminToolsState = {
  generatedAt: string;
  tools: AdminToolCatalogRecord[];
  assignments: AdminToolAgentAssignment[];
  learningSessions: AdminToolLearningSession[];
  consolidationRuns: AdminToolConsolidationRun[];
  source: {
    persistence: "climb_one_core";
    tenantScoped: true;
    workspaceScoped: true;
    rawSecretsVisibleByDefault: false;
  };
};

export type AdminToolAssignmentCreateInput = {
  toolId: string;
  moduleIds: string[];
  agentId: string;
  auditorAgentId?: string;
  visibility?: AdminToolVisibility;
};

export type AdminToolLearningSessionCreateInput = {
  toolId: string;
  moduleId: string;
  assignmentId?: string;
  agentId?: string;
  title: string;
  objective: string;
  teachingMode: AdminToolTeachingMode;
};

export type AdminToolConsolidationRunCreateInput = {
  assignmentId: string;
  agentId: string;
  toolId: string;
  scheduledFor?: string;
};

export type AdminToolCreateInput = {
  displayName: string;
  domainId: string;
  description: string;
  decisionRoomToolDomain?: string;
  visibility?: AdminToolVisibility;
  status?: AdminToolStatus;
  modules: AdminToolModuleContract[];
  allowedRoleIds?: string[];
};

export type AdminToolLearningEvidenceAppendInput = {
  sessionId: string;
  status?: AdminToolLearningSession["status"];
  evidence: AdminToolLearningSession["evidence"];
};

export type AdminToolLearningArtifactStoreInput = {
  sessionId: string;
  kind: AdminToolLearningSession["evidence"][number]["kind"];
  label: string;
  fileName: string;
  mimeType: string;
  contentBase64: string;
  status?: AdminToolLearningSession["status"];
};

export type AdminToolLearningMemoryPromotionInput = {
  sessionId: string;
  promotionStatus: AdminToolLearningSession["memoryWriteback"]["promotionStatus"];
};

export async function loadAdminTools(): Promise<AdminToolsState> {
  const response = await fetch("/admin-console/tools", {
    cache: "no-store",
  });
  const payload = await response.json();

  if (!response.ok || !Array.isArray(payload.tools)) {
    throw new Error(payload.error ?? "admin_tools_failed");
  }

  return {
    generatedAt: payload.generatedAt,
    tools: payload.tools,
    assignments: payload.assignments ?? [],
    learningSessions: payload.learningSessions ?? [],
    consolidationRuns: payload.consolidationRuns ?? [],
    source: payload.source,
  };
}

export async function createAdminTool(
  request: AdminToolCreateInput
): Promise<AdminToolCatalogRecord> {
  const response = await fetch("/admin-console/tools", {
    method: "POST",
    headers: {
      "content-type": "application/json",
    },
    body: JSON.stringify(request),
  });
  const payload = await response.json();

  if (!response.ok || !payload.tool) {
    throw new Error(payload.error ?? "admin_tool_create_failed");
  }

  return payload.tool;
}

export async function createAdminToolAssignment(
  request: AdminToolAssignmentCreateInput
): Promise<AdminToolAgentAssignment> {
  const response = await fetch("/admin-console/tools/assignments", {
    method: "POST",
    headers: {
      "content-type": "application/json",
    },
    body: JSON.stringify(request),
  });
  const payload = await response.json();

  if (!response.ok || !payload.assignment) {
    throw new Error(payload.error ?? "admin_tool_assignment_failed");
  }

  return payload.assignment;
}

export async function createAdminToolLearningSession(
  request: AdminToolLearningSessionCreateInput
): Promise<AdminToolLearningSession> {
  const response = await fetch("/admin-console/tools/learning-sessions", {
    method: "POST",
    headers: {
      "content-type": "application/json",
    },
    body: JSON.stringify(request),
  });
  const payload = await response.json();

  if (!response.ok || !payload.learningSession) {
    throw new Error(payload.error ?? "admin_tool_learning_session_failed");
  }

  return payload.learningSession;
}

export async function appendAdminToolLearningEvidence(
  request: AdminToolLearningEvidenceAppendInput
): Promise<AdminToolLearningSession> {
  const response = await fetch("/admin-console/tools/learning-sessions/evidence", {
    method: "POST",
    headers: {
      "content-type": "application/json",
    },
    body: JSON.stringify(request),
  });
  const payload = await response.json();

  if (!response.ok || !payload.learningSession) {
    throw new Error(payload.error ?? "admin_tool_learning_evidence_failed");
  }

  return payload.learningSession;
}

export async function uploadAdminToolLearningArtifact(
  request: AdminToolLearningArtifactStoreInput
): Promise<AdminToolLearningSession> {
  const response = await fetch("/admin-console/tools/learning-sessions/artifacts", {
    method: "POST",
    headers: {
      "content-type": "application/json",
    },
    body: JSON.stringify(request),
  });
  const payload = await response.json();

  if (!response.ok || !payload.learningSession) {
    throw new Error(payload.error ?? "admin_tool_learning_artifact_failed");
  }

  return payload.learningSession;
}

export async function promoteAdminToolLearningMemory(
  request: AdminToolLearningMemoryPromotionInput
): Promise<AdminToolLearningSession> {
  const response = await fetch("/admin-console/tools/learning-sessions/promote-memory", {
    method: "POST",
    headers: {
      "content-type": "application/json",
    },
    body: JSON.stringify(request),
  });
  const payload = await response.json();

  if (!response.ok || !payload.learningSession) {
    throw new Error(payload.error ?? "admin_tool_learning_promotion_failed");
  }

  return payload.learningSession;
}

export function adminToolLearningArtifactUrl(referenceUri: string): string | null {
  const prefix = "artifact://admin-tools/learning/";
  if (!referenceUri.startsWith(prefix)) return null;
  const path = referenceUri.slice(prefix.length);
  const [sessionId, fileName] = path.split("/");
  if (!sessionId || !fileName) return null;
  return `/admin-console/tools/learning-sessions/artifacts/${encodeURIComponent(sessionId)}/${encodeURIComponent(fileName)}`;
}

export async function createAdminToolConsolidationRun(
  request: AdminToolConsolidationRunCreateInput
): Promise<AdminToolConsolidationRun> {
  const response = await fetch("/admin-console/tools/consolidation-runs", {
    method: "POST",
    headers: {
      "content-type": "application/json",
    },
    body: JSON.stringify(request),
  });
  const payload = await response.json();

  if (!response.ok || !payload.consolidationRun) {
    throw new Error(payload.error ?? "admin_tool_consolidation_failed");
  }

  return payload.consolidationRun;
}

export async function loadAdminAgentRegistry(): Promise<{
  agents: AgentProfile[];
}> {
  const response = await fetch("/admin-console/agents", {
    cache: "no-store",
  });
  const payload = await response.json();

  if (!response.ok || !Array.isArray(payload.agents)) {
    throw new Error(payload.error ?? "admin_agent_registry_failed");
  }

  return {
    agents: payload.agents.map(toAgentProfile),
  };
}

export async function createAdminAgent(
  request: AdminAgentCreateInput
): Promise<AgentProfile> {
  const response = await fetch("/admin-console/agents", {
    method: "POST",
    headers: {
      "content-type": "application/json",
    },
    body: JSON.stringify(request),
  });
  const payload = await response.json();

  if (!response.ok || !payload.agent) {
    throw new Error(payload.error ?? "admin_agent_create_failed");
  }

  return toAgentProfile(payload.agent);
}

export async function registerAdminAgent(agentId: string): Promise<AgentProfile> {
  const response = await fetch("/admin-console/agents/register", {
    method: "POST",
    headers: {
      "content-type": "application/json",
    },
    body: JSON.stringify({ agentId }),
  });
  const payload = await response.json();

  if (!response.ok || !payload.agent) {
    throw new Error(payload.error ?? "admin_agent_register_failed");
  }

  return toAgentProfile(payload.agent);
}

export async function selectDecisionRoomAgentCandidates(topic: {
  id?: string;
  title: string;
  status?: string;
  kind?: string;
  toolDomain?: string;
  taskType?: string;
  origin?: string;
  createdBy?: string;
  participantIds?: string[];
  tags?: string[];
}): Promise<{
  candidates: Array<{
    agentId: string;
    displayName: string;
    role: string;
    domain: string;
    registrationStatus: string;
    score: number;
    reasons: string[];
  }>;
  reasoning: {
    summary: string;
  };
}> {
  const response = await fetch("/admin-console/agents/decision-room-candidates", {
    method: "POST",
    headers: {
      "content-type": "application/json",
    },
    body: JSON.stringify({ topic }),
  });
  const payload = await response.json();

  if (!response.ok || !Array.isArray(payload.candidates)) {
    throw new Error(payload.error ?? "admin_agent_decision_room_candidates_failed");
  }

  return {
    candidates: payload.candidates,
    reasoning: payload.reasoning,
  };
}

function toAuraConversationMessage(
  message: ConversationMessage
): AuraConversationWireMessage {
  return {
    role: message.source === "human" ? "user" : "assistant",
    text: message.content,
    createdAt: message.timestamp,
  };
}

function toAgentProfile(agent: AgentProfile & { schemaVersion?: number }): AgentProfile {
  return {
    identity: agent.identity,
    capability: agent.capability,
    intelligence: agent.intelligence,
    authority: agent.authority,
    memory: agent.memory,
    decisionRoom: agent.decisionRoom,
    tools: agent.tools,
    registrationStatus: agent.registrationStatus,
    registeredAt: agent.registeredAt,
    lastActiveAt: agent.lastActiveAt,
    createdAt: agent.createdAt,
    updatedAt: agent.updatedAt,
  };
}

function toConversationMessage(message: {
  id: string;
  role: "user" | "assistant";
  sourceAgent?: AdminConsoleAgentTarget | "human";
  targetAgent: AdminConsoleAgentTarget;
  respondedBy?: AdminConsoleAgentTarget;
  responderRuntime?: string;
  channelProxy?: boolean;
  text: string;
  createdAt: string;
}): ConversationMessage {
  const targetAgent = fromAdminConsoleAgentTarget(message.targetAgent);
  const respondedBy = message.respondedBy
    ? fromAdminConsoleAgentTarget(message.respondedBy)
    : undefined;
  const source =
    message.role === "user"
      ? "human"
      : fromAdminConsoleAgentTarget(
          message.respondedBy ??
            (message.sourceAgent === "human" || !message.sourceAgent ? "aura" : message.sourceAgent)
        );

  return {
    id: message.id,
    source: source === "all" ? "aura" : source,
    agentName:
      message.role === "assistant"
        ? agentLabel(respondedBy ?? (source === "human" ? "aura" : source))
        : undefined,
    content: message.text,
    timestamp: message.createdAt,
    metadata: {
      coreEvent: "admin_console_aura_conversation",
      targetAgent,
      respondedBy,
      responderRuntime: message.responderRuntime,
      channelProxy: message.channelProxy,
    },
  };
}

function fromAdminConsoleAgentTarget(target: AdminConsoleAgentTarget): AgentType | "all" {
  const agents: Record<AdminConsoleAgentTarget, AgentType | "all"> = {
    aura: "aura",
    founder_agent: "founder-agent",
    codex: "codex",
    claude: "claude",
    core: "core",
    all_agents: "all",
  };

  return agents[target];
}

function agentLabel(agent: AgentType | "all"): string {
  const labels: Record<AgentType | "all", string> = {
    aura: "AURA",
    "founder-agent": "Founder Agent",
    codex: "Codex",
    claude: "Claude",
    core: "Core",
    all: "All Agents",
  };

  return labels[agent];
}
